import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Share2, X, BadgeCheck } from 'lucide-react';
import verifiedScreen from 'assets/images/home/verified_screen.png';
import styles from './VerifiedShareModal.module.scss';
import useWindowSize from 'utils/useWindowSize';
import { toastActions } from 'Containers/Toasts/store';
import {
  canvasToShareFile,
  composeStoryShareCanvasFromImage,
  runStoryShareFlow,
} from 'utils/storyShare';

const VerifiedShareModal = ({ closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const [sharing, setSharing] = useState(false);
  const shareFileRef = useRef(null);
  const shareFilePromiseRef = useRef(null);

  const buildShareFile = async () => {
    const baseName =
      t('home.infoCards.verifiedSalon.downloadFileName', {
        defaultValue: 'zetabarber-salone-verificato.jpg',
      }).replace(/\.(png|jpe?g)$/i, '') || 'zetabarber-salone-verificato';

    // Immagine già 1080×1920: la ricomponiamo su frame Stories (JPEG share-friendly)
    const storyCanvas = await composeStoryShareCanvasFromImage(verifiedScreen, {
      background: '#000000',
      pad: 0,
    });
    return canvasToShareFile(storyCanvas, baseName);
  };

  const ensureShareFile = () => {
    if (shareFileRef.current) return Promise.resolve(shareFileRef.current);
    if (!shareFilePromiseRef.current) {
      shareFilePromiseRef.current = buildShareFile()
        .then((file) => {
          shareFileRef.current = file;
          return file;
        })
        .catch((err) => {
          shareFilePromiseRef.current = null;
          throw err;
        });
    }
    return shareFilePromiseRef.current;
  };

  useEffect(() => {
    shareFileRef.current = null;
    shareFilePromiseRef.current = null;
    const timer = window.setTimeout(() => {
      ensureShareFile().catch(() => {});
    }, 120);
    return () => window.clearTimeout(timer);
  }, []);

  const showShareToast = (title, warning = true) => {
    dispatch(
      toastActions.OPEN_TOAST.SUCCESS({
        type: 'worker',
        props: { title, warning },
      }),
    );
  };

  const handleShare = async () => {
    if (sharing) return;

    try {
      setSharing(true);
      const file = await ensureShareFile();
      const outcome = await runStoryShareFlow({
        file,
        isMobile,
        onToast: (kind) => {
          showShareToast(
            kind === 'retry'
              ? t('scheduler.workerSoldOutModal.shareRetryHint')
              : kind === 'photos'
                ? t('scheduler.workerSoldOutModal.shareSaveToPhotosHint')
                : t('scheduler.workerSoldOutModal.shareSavedFallback'),
            false,
          );
        },
      });

      if (outcome === 'shared' || outcome === 'downloaded') {
        closeModal?.();
      }
    } catch (err) {
      console.warn('VerifiedShare share failed', err);
      showShareToast(t('scheduler.workerSoldOutModal.shareError'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.header_text}>
          <span className={styles.eyebrow}>
            <BadgeCheck size={14} strokeWidth={2.5} aria-hidden />
            {t('home.infoCards.verifiedSalon.excellenceVerified')}
          </span>
          <h2 className={styles.title}>
            {t('home.infoCards.verifiedSalon.shareModalTitle')}
          </h2>
          <p className={styles.subtitle}>
            {t('home.infoCards.verifiedSalon.shareModalSubtitle')}
          </p>
        </div>
        {closeModal ? (
          <button
            type="button"
            className={styles.close_btn}
            onClick={closeModal}
            aria-label={t('common.actions.close', { defaultValue: 'Chiudi' })}
          >
            <X size={18} strokeWidth={2.25} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className={styles.preview}>
        <div className={styles.preview_frame}>
          <img
            src={verifiedScreen}
            alt={t('home.infoCards.verifiedSalon.shareModalTitle')}
            className={styles.image}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.download_btn}
          onPointerDown={() => {
            ensureShareFile().catch(() => {});
          }}
          onClick={handleShare}
          disabled={sharing}
        >
          <Share2 size={18} strokeWidth={2.25} aria-hidden />
          <span>
            {sharing
              ? t('scheduler.workerSoldOutModal.preparing')
              : t('home.infoCards.verifiedSalon.shareCta')}
          </span>
        </button>
      </div>
    </div>
  );
};

export default VerifiedShareModal;
